import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, TrendingUp, Network, Package, Zap, BarChart3, RefreshCw, Globe } from "lucide-react";
import OptimizedImage from "@/components/ImageOptimization";
import analyticsImage from "@/assets/analytics-platform-dashboard.jpg";

interface ServiceModule {
  title: string;
  description: string;
  overview: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  implementation: {
    strategy: string[];
    configuration: string[];
    optimization: string[];
  };
  modules?: string[];
}

const kinaxisModules: Record<string, ServiceModule> = {
  "rapidresponse": {
    title: "RapidResponse Platform",
    description: "Real-time supply chain planning and collaboration with concurrent planning capabilities.",
    overview: "Kinaxis RapidResponse is a cloud-based supply chain management platform that enables organizations to orchestrate their entire supply chain network with real-time visibility, collaboration, and advanced analytics.",
    features: ["Concurrent Planning", "What-if Analysis", "Real-time Collaboration", "Advanced Analytics", "Supply Chain Visibility", "Scenario Modeling"],
    technologies: ["Kinaxis RapidResponse", "Cloud Platform", "Advanced Analytics", "Machine Learning", "Integration APIs", "Supply Chain Intelligence"],
    benefits: ["Improved Responsiveness", "Reduced Costs", "Better Visibility", "Enhanced Collaboration", "Risk Mitigation", "Optimized Inventory"],
    process: ["Assessment & Strategy", "Platform Configuration", "Data Integration", "User Training", "Go-Live", "Continuous Optimization"],
    implementation: {
      strategy: ["Supply Chain Assessment", "Business Process Mapping", "Integration Strategy", "Change Management", "KPI Definition"],
      configuration: ["Platform Setup", "Module Configuration", "Workflow Design", "Alert Rules", "Dashboard Creation"],
      optimization: ["Performance Tuning", "Advanced Analytics", "Predictive Modeling", "Process Refinement", "ROI Tracking"]
    },
    modules: ["Demand Planning", "Supply Planning", "S&OP", "MPS/MRP", "Inventory Management", "Order Fulfillment"]
  },
  "demand-planning": {
    title: "Demand Planning",
    description: "Statistical forecasting and demand sensing with machine learning capabilities.",
    overview: "Kinaxis Demand Planning enables organizations to create accurate demand forecasts using advanced statistical models, machine learning, and real-time market intelligence.",
    features: ["Statistical Forecasting", "Demand Sensing", "Promotional Planning", "New Product Introduction", "Forecast Collaboration", "Exception Management"],
    technologies: ["Kinaxis ML Models", "Statistical Algorithms", "Demand Intelligence", "Forecast Analytics", "Cloud Computing", "Data Integration"],
    benefits: ["Forecast Accuracy", "Reduced Stockouts", "Lower Inventory", "Better Planning", "Faster Response", "Enhanced Collaboration"],
    process: ["Data Collection", "Model Selection", "Forecast Generation", "Collaboration", "Consensus Planning", "Continuous Improvement"],
    implementation: {
      strategy: ["Forecast Strategy", "Data Source Identification", "Model Selection", "Collaboration Framework", "Performance Metrics"],
      configuration: ["Data Integration", "Model Configuration", "Workflow Setup", "Collaboration Tools", "Exception Alerts"],
      optimization: ["Model Tuning", "Accuracy Improvement", "Process Enhancement", "Advanced Analytics", "Predictive Insights"]
    }
  },
  "supply-planning": {
    title: "Supply Planning & Optimization",
    description: "Capacity planning, constraint-based optimization, and supplier collaboration.",
    overview: "Kinaxis Supply Planning provides comprehensive tools for optimizing production schedules, managing constraints, and ensuring supply meets demand across your entire network.",
    features: ["Capacity Planning", "Constraint Management", "Production Scheduling", "Supplier Collaboration", "Multi-site Planning", "Optimization Algorithms"],
    technologies: ["Constraint-based Planning", "Optimization Engine", "Multi-echelon Planning", "Supply Network Modeling", "Real-time Data", "Advanced Analytics"],
    benefits: ["Optimized Production", "Reduced Lead Times", "Better Utilization", "Lower Costs", "Improved Agility", "Enhanced Visibility"],
    process: ["Network Modeling", "Constraint Definition", "Optimization", "Schedule Generation", "Collaboration", "Execution Monitoring"],
    implementation: {
      strategy: ["Network Analysis", "Constraint Mapping", "Optimization Strategy", "Collaboration Model", "Performance Framework"],
      configuration: ["Network Setup", "Constraint Configuration", "Optimization Rules", "Workflow Design", "Integration Setup"],
      optimization: ["Algorithm Tuning", "Performance Enhancement", "Advanced Scenarios", "Predictive Planning", "Continuous Improvement"]
    }
  },
  "sales-operations": {
    title: "Sales & Operations Planning (S&OP)",
    description: "Integrated business planning with financial reconciliation and scenario analysis.",
    overview: "Kinaxis S&OP enables organizations to align their operational and financial plans through integrated business planning, scenario modeling, and executive collaboration.",
    features: ["Integrated Planning", "Financial Reconciliation", "Scenario Modeling", "Executive Dashboards", "Gap Analysis", "Consensus Planning"],
    technologies: ["S&OP Platform", "Financial Integration", "Scenario Engine", "Analytics & BI", "Collaboration Tools", "Executive Reporting"],
    benefits: ["Aligned Plans", "Better Decisions", "Financial Accuracy", "Risk Visibility", "Faster Cycles", "Executive Buy-in"],
    process: ["Data Gathering", "Demand Review", "Supply Review", "Financial Reconciliation", "Executive Review", "Plan Finalization"],
    implementation: {
      strategy: ["S&OP Framework", "Process Design", "Financial Integration", "Governance Model", "Meeting Cadence"],
      configuration: ["Planning Cycle Setup", "Financial Links", "Scenario Tools", "Dashboards", "Workflow Automation"],
      optimization: ["Process Maturity", "Advanced Analytics", "Predictive Modeling", "Best Practices", "Continuous Enhancement"]
    }
  }
};

const KinaxisDetail = () => {
  const { moduleId } = useParams();
  const module = moduleId ? kinaxisModules[moduleId] : null;
  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleId]);

  const toggleModule = (key: string) => {
    setExpandedModules(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  if (moduleId && !module) {
    return <Navigate to="/404" replace />;
  }

  const getIcon = (moduleId: string) => {
    const icons: Record<string, any> = {
      "rapidresponse": Network,
      "demand-planning": TrendingUp,
      "supply-planning": Package,
      "sales-operations": BarChart3
    };
    return icons[moduleId] || Network;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src={analyticsImage}
            alt="Kinaxis Supply Chain Planning"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/90" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">
              Supply Chain Planning
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
              {module ? module.title : "Kinaxis Supply Chain Solutions"}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              {module 
                ? module.description 
                : "Transform your supply chain with AI-powered planning, real-time visibility, and collaborative decision-making"}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      {module && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">Overview</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {module.overview}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="h-5 w-5 text-primary shadow-glow" />
                      Key Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <span className={`${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <TrendingUp className="h-5 w-5 text-secondary shadow-glow-secondary" />
                      Benefits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <span className={`${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Modules Grid (when no specific module selected) */}
      {!module && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
              Kinaxis Capabilities
            </h2>
            
            <div className="space-y-6 max-w-6xl mx-auto">
              {Object.entries(kinaxisModules).map(([key, mod]) => {
                const Icon = getIcon(key);
                const isExpanded = expandedModules.includes(key);
                return (
                  <Card key={key} className="border-border/50 hover:shadow-lg transition-all duration-300">
                    <Collapsible open={isExpanded} onOpenChange={() => toggleModule(key)}>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`p-2 ${Object.keys(kinaxisModules).indexOf(key) % 2 === 0 ? 'bg-primary/10 shadow-glow' : 'bg-secondary/10 shadow-glow-secondary'} rounded-lg`}>
                              <Icon className={`h-6 w-6 ${Object.keys(kinaxisModules).indexOf(key) % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'}`} />
                            </div>
                            <div className="flex-1">
                              <CardTitle className="text-foreground mb-2">{mod.title}</CardTitle>
                              <p className="text-muted-foreground">{mod.description}</p>
                            </div>
                          </div>
                          <CollapsibleTrigger asChild>
                            <Button variant="outline" size="sm">
                              {isExpanded ? "Show Less" : "Learn More"}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                      </CardHeader>
                      
                      <CollapsibleContent>
                        <CardContent className="pt-0 space-y-6">
                          {/* Overview */}
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-3">Overview</h3>
                            <p className="text-muted-foreground leading-relaxed">{mod.overview}</p>
                          </div>

                          {/* Features and Benefits Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary shadow-glow" />
                                Key Features
                              </h4>
                              <ul className="space-y-2">
                                {mod.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className={`${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-secondary shadow-glow-secondary" />
                                Benefits
                              </h4>
                              <ul className="space-y-2">
                                {mod.benefits.map((benefit, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className={`${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                                    <span>{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* Implementation Approach */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-4">Implementation Approach</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Strategy</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.strategy.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Configuration</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.configuration.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Optimization</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.optimization.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Technologies */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-3">Technologies</h4>
                            <div className="flex flex-wrap gap-2">
                              {mod.technologies.map((tech, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Implementation Section */}
      {module && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                Implementation Approach
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Strategy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.strategy.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.configuration.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Optimization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.optimization.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary/10 via-secondary/5 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
              Ready to Transform Your Supply Chain?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's discuss how Kinaxis can optimize your supply chain operations
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 shadow-glow">
              <a href="/#contact">Get Started</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KinaxisDetail;
